import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  CardActions,
  LinearProgress,
  Tooltip,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import LockIcon from "@mui/icons-material/Lock";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useNavigate } from "react-router-dom";
import { usePreventivoList } from "../../hooks/usePreventivoList";
import { preventiviApi, Preventivo } from "../../services/preventiviApi";
import { PreventivoStatusChip, CategoryChip } from "../../components/common/StatusChip";
import { UploadDialog } from "../../components/cost-estimation/UploadDialog";
import { CATEGORIES } from "../../services/configApi";
import { useQueryClient } from "react-query";
import toast from "react-hot-toast";
import { formatDateRome } from "../../utils/dateFormat";
import { useAuth } from "../../contexts/AuthContext";

const STATUS_OPTIONS = ["open", "processing", "ready", "error", "closed"];

type SortField = "name" | "created_at" | "anno" | "total_rows";
type SortDir = "asc" | "desc";

export const PreventivoListPage: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const canDeletePreventivo =
    user?.role === "admin" || user?.role === "mantainer";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [categoriaFilter, setCategoriaFilter] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [sortField, setSortField] = useState<SortField>("created_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const filterParam = statusFilter.length > 0 ? statusFilter.join(",") : undefined;
  const { data: rawPreventivi, isLoading, error } = usePreventivoList({
    q,
    status_filter: filterParam,
    categoria: categoriaFilter || undefined,
  });

  // Client-side sort
  const preventivi = rawPreventivi
    ? [...rawPreventivi].sort((a, b) => {
        let cmp = 0;
        if (sortField === "name") cmp = a.name.localeCompare(b.name);
        else if (sortField === "created_at") cmp = (a.created_at ?? "").localeCompare(b.created_at ?? "");
        else if (sortField === "anno") cmp = (a.anno ?? 0) - (b.anno ?? 0);
        else if (sortField === "total_rows") cmp = a.total_rows - b.total_rows;
        return sortDir === "asc" ? cmp : -cmp;
      })
    : rawPreventivi;

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  };

  const handleCreated = (id: string) => {
    setUploadOpen(false);
    qc.invalidateQueries("preventivi");
    navigate(`/cost-estimation/${id}`);
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDeleteId || !canDeletePreventivo) return;
    setDeleting(true);
    try {
      await preventiviApi.delete(confirmDeleteId);
      qc.invalidateQueries("preventivi");
      toast.success("Preventivo eliminato");
      setConfirmDeleteId(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Errore nell'eliminazione");
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusToggle = (_: any, newValues: string[]) => setStatusFilter(newValues);

  const content: React.ReactNode = (
    <>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 4, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Preventivi</Typography>
          <Typography variant="body2" color="text.secondary">
            Gestisci le stime di costo BOM
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setUploadOpen(true)}
          sx={{ borderRadius: "10px", px: 2.5 }}
        >
          Nuovo preventivo
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 3, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          placeholder="Cerca per nome, cantiere, codice..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          size="small"
          sx={{ width: 280 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: "1.1rem", color: "text.disabled" }} />
                </InputAdornment>
              ),
              sx: { borderRadius: "10px" },
            },
          }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Categoria</InputLabel>
          <Select
            value={categoriaFilter}
            label="Categoria"
            onChange={(e) => setCategoriaFilter(e.target.value)}
            sx={{ borderRadius: "10px" }}
          >
            <MenuItem value=""><em>Tutte</em></MenuItem>
            {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
        <ToggleButtonGroup
          value={statusFilter}
          onChange={handleStatusToggle}
          size="small"
          sx={{ "& .MuiToggleButton-root": { borderRadius: "8px !important", px: 1.5, fontSize: "0.78rem", textTransform: "none" } }}
        >
          {STATUS_OPTIONS.map((s) => (
            <ToggleButton key={s} value={s}>
              <PreventivoStatusChip status={s} />
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Content */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Errore nel caricamento dei preventivi.
        </Alert>
      )}

      {!isLoading && preventivi?.length === 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: 10,
            gap: 2.5,
            color: "text.disabled",
          }}
        >
          <InsertDriveFileOutlinedIcon sx={{ fontSize: "4rem" }} />
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
            Nessun preventivo
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Carica il tuo primo file BOM per iniziare.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setUploadOpen(true)}
            sx={{ borderRadius: "10px", mt: 1 }}
          >
            Nuovo preventivo
          </Button>
        </Box>
      )}

      {preventivi && preventivi.length > 0 && (
        <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: "14px", overflow: "hidden" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "rgba(0,0,0,0.02)" }}>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem" }}>
                  <TableSortLabel
                    active={sortField === "name"}
                    direction={sortField === "name" ? sortDir : "asc"}
                    onClick={() => handleSort("name")}
                  >
                    Nome
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem" }}>Stato</TableCell>
                {!isMobile && <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem" }}>Categoria</TableCell>}
                {!isMobile && (
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem" }}>
                    <TableSortLabel
                      active={sortField === "anno"}
                      direction={sortField === "anno" ? sortDir : "asc"}
                      onClick={() => handleSort("anno")}
                    >
                      Anno
                    </TableSortLabel>
                  </TableCell>
                )}
                {!isMobile && <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem" }}>Codice / Cantiere</TableCell>}
                <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem" }}>
                  <TableSortLabel
                    active={sortField === "total_rows"}
                    direction={sortField === "total_rows" ? sortDir : "asc"}
                    onClick={() => handleSort("total_rows")}
                  >
                    Righe
                  </TableSortLabel>
                </TableCell>
                {!isMobile && (
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem" }}>
                    <TableSortLabel
                      active={sortField === "created_at"}
                      direction={sortField === "created_at" ? sortDir : "asc"}
                      onClick={() => handleSort("created_at")}
                    >
                      Data
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.78rem" }}>Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {preventivi.map((p: Preventivo) => {
                const isReadonly = p.status === "closed";
                return (
                  <TableRow
                    key={p.id}
                    hover
                    sx={{
                      cursor: "pointer",
                      opacity: isReadonly ? 0.75 : 1,
                      "&:last-child td": { border: 0 },
                    }}
                    onClick={() => navigate(`/cost-estimation/${p.id}`)}
                  >
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        {isReadonly && <LockIcon sx={{ fontSize: "0.8rem", color: "text.disabled", flexShrink: 0 }} />}
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{p.name}</Typography>
                      </Box>
                      {p.status === "processing" && (
                        <LinearProgress
                          variant={p.total_rows > 0 ? "determinate" : "indeterminate"}
                          value={p.total_rows > 0 ? (p.processed_rows / p.total_rows) * 100 : undefined}
                          sx={{ height: 3, borderRadius: 2, mt: 0.5, width: "90%" }}
                        />
                      )}
                    </TableCell>
                    <TableCell><PreventivoStatusChip status={p.status} /></TableCell>
                    {!isMobile && (
                      <TableCell>
                        {p.categoria ? <CategoryChip categoria={p.categoria} /> : <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                    )}
                    {!isMobile && (
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">{p.anno ?? "—"}</Typography>
                      </TableCell>
                    )}
                    {!isMobile && (
                      <TableCell sx={{ maxWidth: 220 }}>
                        {p.codice_progetto && (
                          <Chip label={p.codice_progetto} size="small" variant="outlined" sx={{ height: 18, fontSize: "0.65rem", mr: 0.5 }} />
                        )}
                        {p.nome_cantiere && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", maxWidth: 200 }}>
                            {p.nome_cantiere}
                          </Typography>
                        )}
                        {!p.codice_progetto && !p.nome_cantiere && <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{p.total_rows}</Typography>
                    </TableCell>
                    {!isMobile && (
                      <TableCell>
                        <Typography variant="caption" color="text.disabled">
                          {formatDateRome(p.created_at) ?? "—"}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                        <Tooltip title="Apri">
                          <IconButton size="small" onClick={() => navigate(`/cost-estimation/${p.id}`)}>
                            <FolderOpenIcon sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        </Tooltip>
                        {p.status !== "closed" && (
                          <Tooltip
                            title={
                              canDeletePreventivo
                                ? "Elimina"
                                : "Solo admin e maintainer possono eliminare"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={!canDeletePreventivo}
                                onClick={() => {
                                  if (canDeletePreventivo) setConfirmDeleteId(p.id);
                                }}
                              >
                                <DeleteOutlineIcon sx={{ fontSize: "1rem" }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onCreated={handleCreated}
      />

      {/* Delete confirm dialog */}
      <Dialog
        open={!!confirmDeleteId}
        onClose={() => !deleting && setConfirmDeleteId(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "1rem" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
          <WarningAmberIcon sx={{ color: "#E65100" }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Elimina preventivo
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Sei sicuro di voler eliminare questo preventivo? Tutte le righe associate verranno rimosse.
            <br />
            <strong>Questa operazione è irreversibile.</strong>
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmDeleteId(null)} disabled={deleting}>
            Annulla
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : "Elimina"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, width: "100%" }}>
      {content}
    </Box>
  );
};
